import React from 'react';
import { TableSummaryBySchoolProps, TableSummaryBySchoolData, OlympiadStatus } from '../../model';
import {
  Table,
  Tr,
  Th,
  Sort,
  ThContent,
  SubheaderThContent,
  SubheaderText,
  Tbody,
  Municipality,
  School,
  Regisrations,
  Participants,
  Winners,
  Thead,
  Tfoot,
  TotalLabel,
  TableWrap,
} from './SummaryBySchoolStyled';
import { useSorter } from './hooks';
import TableRowWithTooltip from 'features/shared/TableRowWithTooltip';
import { numberWithSpaces } from 'utils/numberWithSpaces';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import pluralize from 'utils/pluralize';

const createRow = (
  data: TableSummaryBySchoolData,
  hasOlympiadPassed: boolean,
  isRegionUser: boolean,
  __isRoads221: boolean,
) => {
  return (
    <Tr key={data.municipalicy + data.schoolTitle}>
      <Municipality>
        <TableRowWithTooltip>{data.municipalicy}</TableRowWithTooltip>
      </Municipality>
      {!isRegionUser && (
        <School>
          <TableRowWithTooltip>{data.schoolTitle}</TableRowWithTooltip>
        </School>
      )}
      {!__isRoads221 && <Regisrations>{numberWithSpaces(data.activeStudentsCount || 0)}</Regisrations>}
      <Participants>{numberWithSpaces(data.participantsCount || 0)} </Participants>

      {hasOlympiadPassed && <Winners>{numberWithSpaces(data.prizeholdersCount || 0)} </Winners>}
    </Tr>
  );
};

const TableSummaryBySchool = (props: TableSummaryBySchoolProps) => {
  const total = props.total;
  const { sortRows, ...sortingState } = useSorter(props.rows || [], [
    'municipalicy',
    'activeStudentsCount',
    'participantsCount',
    'prizeholdersCount',
  ]);
  const hasOlympiadPassed = props.status === OlympiadStatus.passed;
  const { logPageEvent } = useClickStreamContext();
  const studentColCount = 1 + (hasOlympiadPassed ? 1 : 0) + (!props.__isRoads2211 ? 1 : 0);
  const studentColWidth = `${50 / studentColCount}%`;

  return (
    <TableWrap data-testid="summary-by-school-table">
      <Table $isRegionUser={props.isRegionUser}>
        <colgroup>
          <col style={{ width: props.isRegionUser ? '50%' : '25%' }} />
          {!props.isRegionUser && <col style={{ width: '25%' }} />}
          {!props.__isRoads2211 && <col style={{ width: studentColWidth }} />}
          <col style={{ width: studentColWidth }} />
          {hasOlympiadPassed && <col style={{ width: studentColWidth }} />}
        </colgroup>
        <Thead>
          <Tr style={{ height: 32 }}>
            <Municipality as={Th} rowSpan={2}>
              <ThContent>
                Муниципалитет/
                <br />
                регион
                <LogClick
                  eventType="olymp_sorting_table"
                  eventInfo="Олимпиада: Сортировка таблицы по полю 'Муниципалитет/регион'"
                  cd1={props.olympiadId}
                >
                  <Sort {...sortingState.municipalicy} data-testid="sort-municipality" />
                </LogClick>
              </ThContent>
            </Municipality>
            {!props.isRegionUser && (
              <School as={Th} rowSpan={2}>
                Школа
              </School>
            )}
            <Th colSpan={hasOlympiadPassed ? 3 : 2}>Ученики</Th>
          </Tr>
          <Tr style={{ height: 48 }}>
            {!props.__isRoads2211 && (
              <Regisrations>
                <SubheaderThContent>
                  <SubheaderText>
                    Занимаются <br />
                    на Учи.ру
                  </SubheaderText>
                  <LogClick
                    eventType="olymp_sorting_table"
                    eventInfo="Олимпиада: Сортировка таблицы по полю 'Активных на Учи.ру'"
                    cd1={props.olympiadId}
                  >
                    <Sort {...sortingState.activeStudentsCount} data-testid="sort-active-students" />
                  </LogClick>
                </SubheaderThContent>
              </Regisrations>
            )}
            <Participants>
              <SubheaderThContent>
                <SubheaderText>Участники</SubheaderText>
                <LogClick
                  eventType="olymp_sorting_table"
                  eventInfo="Олимпиада: Сортировка таблицы по полю 'Участники'"
                  cd1={props.olympiadId}
                >
                  <Sort {...sortingState.participantsCount} data-testid="sort-participants" />
                </LogClick>
              </SubheaderThContent>
            </Participants>
            {hasOlympiadPassed && (
              <Winners>
                <SubheaderThContent>
                  <SubheaderText>Победители</SubheaderText>
                  <LogClick
                    eventType="olymp_sorting_table"
                    eventInfo="Олимпиада: Сортировка таблицы по полю 'Победители'"
                    cd1={props.olympiadId}
                  >
                    <Sort {...sortingState.prizeholdersCount} data-testid="sort-prizeholders" />
                  </LogClick>
                </SubheaderThContent>
              </Winners>
            )}
          </Tr>
        </Thead>
        <Tbody>{sortRows.map((el) => createRow(el, hasOlympiadPassed, props.isRegionUser, props.__isRoads2211))}</Tbody>
        <Tfoot>
          <tr>
            <TotalLabel colSpan={props.isRegionUser ? 1 : 2}>
              {props.total.realStudentsCount
                ? `Итого, из ${props.total.realStudentsCount} ${pluralize(props.total.realStudentsCount, [
                    'учащегося',
                    'учащихся',
                    'учащихся',
                  ])} муниципалитета:`
                : 'Итого:'}
            </TotalLabel>
            {!props.__isRoads2211 && <Regisrations>{numberWithSpaces(total.activeStudentsCount || 0)}</Regisrations>}
            <Participants>{numberWithSpaces(total.participantsCount || 0)} </Participants>
            {hasOlympiadPassed && <Winners>{numberWithSpaces(total.prizeholdersCount || 0)}</Winners>}
          </tr>
        </Tfoot>
      </Table>
    </TableWrap>
  );
};
export default TableSummaryBySchool;
